import { useState } from 'react';
import { toast } from 'sonner';
import { admin, apiErrorMessage, type LarcQueueItem, type ReviewVerdict } from '../../lib/api';
import { useApi } from '../../hooks/useApi';
import { useTranslation } from '../../i18n';
import { DataTable, StatusBadge, Button, type Column } from '../../components/ui';
import { useAdminT } from './adminI18n';

export default function AdminReviewPage() {
  const t = useAdminT();
  const { t: gt } = useTranslation();
  const [refreshKey, setRefreshKey] = useState(0);
  const [selected, setSelected] = useState<LarcQueueItem | null>(null);
  const [confidence, setConfidence] = useState('0.8');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const q = useApi(() => admin.reviewQueue(undefined, 100), [refreshKey]);
  const rows = q.status === 'success' ? q.data : [];

  async function decide(verdict: ReviewVerdict) {
    if (!selected) return;
    if ((verdict === 'rejected' || verdict === 'revision_requested') && !notes.trim()) {
      toast.error(t('reason')); return;
    }
    setBusy(true);
    try {
      await admin.reviewDecision(selected.id, {
        verdict,
        confidence: Number(confidence) || null,
        notes: notes.trim() || null,
      });
      toast.success(t('review.done'));
      setSelected(null); setNotes('');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(apiErrorMessage(err, gt));
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<LarcQueueItem>[] = [
    { key: 'question_code', header: t('col.code'), mono: true, width: '160px',
      render: (r) => r.question_code.slice(0, 18) },
    { key: 'tier', header: t('col.tier'), width: '56px', mono: true },
    { key: 'category', header: t('col.category'), width: '120px', render: (r) => r.category ?? '—' },
    { key: 'stem', header: t('col.stem'), render: (r) => r.stem },
    { key: 'review_status', header: t('col.status'), width: '120px',
      render: (r) => <StatusBadge status={r.review_status} /> },
  ];

  return (
    <div className="admin-page">
      <header className="admin-page__head">
        <h1>{t('review.title')}</h1>
        <p className="admin-page__sub">{t('review.subtitle')}</p>
      </header>

      <div className="admin-md">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          onRowClick={(r) => { setSelected(r); setNotes(''); setConfidence('0.8'); }}
          selectedKey={selected?.id ?? null}
          loading={q.status === 'loading'}
          empty={t('allClear')}
        />

        <aside className="admin-detail">
          {!selected ? (
            <p className="admin-detail__hint">{t('review.selectHint')}</p>
          ) : (
            <>
              <h3>{selected.question_code}</h3>
              <p className="admin-detail__stem">{selected.stem}</p>
              <div className="admin-field">
                <span className="admin-field__label">{t('review.confidence')}</span>
                <input
                  type="number" min={0} max={1} step={0.05} value={confidence}
                  onChange={(e) => setConfidence(e.target.value)} className="prop-choice__text"
                />
              </div>
              <div className="admin-field">
                <span className="admin-field__label">{t('notes')}</span>
                <textarea className="prop-textarea" rows={2} value={notes}
                  onChange={(e) => setNotes(e.target.value)} />
              </div>
              <div className="admin-detail__actions">
                <Button size="sm" variant="accent" disabled={busy} onClick={() => decide('approved')}>
                  {t('action.approve')}
                </Button>
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => decide('revision_requested')}>
                  {t('action.revise')}
                </Button>
                <Button size="sm" variant="danger" disabled={busy} onClick={() => decide('rejected')}>
                  {t('action.reject')}
                </Button>
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
